// EAGER SpatiaLite (gaia) + GeoPackage BLOB header sniffers. Split out of sniffWalkers.ts to keep
// each file under the 200-line cap. Same bundle-isolation rule: no wkx/, decoder, proj4 or maplibre
// import. Reuses the shared WKB reader/header/walk helpers from sniffWalkers.ts.

import {
  createReader,
  dimsLabel,
  factsFromWkbHeader,
  geomName,
  optionalCount,
  walkWkbBody,
  type Reader,
  type SniffFacts,
} from './sniffWalkers';

// --- SpatiaLite (gaia) BLOB (SPATIAL-FACTS e1): 0x00 START, endian, SRID, 32-byte MBR, 0x7C mark,
// class-type (int32, 1000/2000/3000 dim offset like WKB), body, 0xFE END. ---

function gaiaBaseDims(ct: number): { base: number; hasZ: boolean; hasM: boolean } {
  if (ct >= 3000 && ct < 4000) return { base: ct - 3000, hasZ: true, hasM: true };
  if (ct >= 2000 && ct < 3000) return { base: ct - 2000, hasZ: false, hasM: true };
  if (ct >= 1000 && ct < 2000) return { base: ct - 1000, hasZ: true, hasM: false };
  return { base: ct, hasZ: false, hasM: false };
}

function gaiaCount(r: Reader, base: number, dpp: number): number {
  switch (base) {
    case 1:
      r.skip(dpp * 8);
      return 1; // gaia never materialises an EMPTY point
    case 2: {
      const n = r.u32();
      r.skip(n * dpp * 8);
      return n;
    }
    case 3: {
      const rings = r.u32();
      let sum = 0;
      for (let i = 0; i < rings; i += 1) {
        const np = r.u32();
        r.skip(np * dpp * 8);
        sum += np;
      }
      return sum;
    }
    case 4:
    case 5:
    case 6:
    case 7: {
      const n = r.u32();
      let sum = 0;
      for (let i = 0; i < n; i += 1) {
        r.skip(1); // 0x69 entity marker
        const bd = gaiaBaseDims(r.u32());
        sum += gaiaCount(r, bd.base, 2 + (bd.hasZ ? 1 : 0) + (bd.hasM ? 1 : 0));
      }
      return sum;
    }
    default:
      throw new Error('non-walkable gaia geometry');
  }
}

export function sniffSpatialite(bytes: Uint8Array, walk: boolean, byteLength: number): SniffFacts | null {
  if (bytes.byteLength < 44 || bytes[0] !== 0x00) return null;
  if (bytes[1] !== 0x00 && bytes[1] !== 0x01) return null;
  if (bytes[38] !== 0x7c) return null; // gaia MBR mark — a strong signature
  const r = createReader(bytes);
  r.skip(2);
  r.setLE(bytes[1] === 0x01);
  const srid = r.u32();
  r.skip(32 + 1); // MBR (4×f64) + 0x7C
  const { base, hasZ, hasM } = gaiaBaseDims(r.u32());
  const geomType = geomName(base);
  if (!geomType) return null;
  return {
    container: 'spatialite',
    geomType,
    srid,
    dimensions: dimsLabel(hasZ, hasM),
    pointCount: optionalCount(walk, geomType, () => gaiaCount(r, base, 2 + (hasZ ? 1 : 0) + (hasM ? 1 : 0))),
    byteLength,
  };
}

// --- GeoPackage BLOB (SPATIAL-FACTS e2): 'GP' + version + flags + SRID + envelope + standard WKB. ---

const GPKG_ENVELOPE_BYTES: Record<number, number> = { 0: 0, 1: 32, 2: 48, 3: 48, 4: 64 };

export function sniffGpkg(bytes: Uint8Array, walk: boolean, byteLength: number): SniffFacts | null {
  if (bytes.byteLength < 8 || bytes[0] !== 0x47 || bytes[1] !== 0x50) return null;
  const flags = bytes[3];
  const envBytes = GPKG_ENVELOPE_BYTES[(flags >> 1) & 0x07];
  if (envBytes === undefined) return null;
  const r = createReader(bytes);
  r.skip(4); // 'GP', version, flags
  r.setLE((flags & 0x01) === 1);
  const srid = r.u32();
  r.skip(envBytes);
  const f = factsFromWkbHeader(r, false); // inner standard WKB
  if (!f) return null;
  return {
    container: 'gpkg',
    geomType: f.geomType,
    srid, // SRID lives in the gpkg header, not the inner WKB
    dimensions: dimsLabel(f.header.hasZ, f.header.hasM),
    pointCount: optionalCount(walk, f.geomType, () => walkWkbBody(r, f.header)),
    byteLength,
  };
}
